import {
  type Column,
  describeRange,
  parseRangeText,
  type RangeSpec,
  type Row,
  type TemplateDoc,
  type TestRow,
} from '@quickreport/domain';

/** Replace one row by id. */
export function updateRow(doc: TemplateDoc, rowId: string, patch: (r: Row) => Row): TemplateDoc {
  return { ...doc, rows: doc.rows.map((r) => (r.id === rowId ? patch(r) : r)) };
}

export function updateTest(doc: TemplateDoc, rowId: string, patch: Partial<TestRow>): TemplateDoc {
  return updateRow(doc, rowId, (r) => (r.kind === 'test' ? { ...r, ...patch } : r));
}

export function updateColumn(
  doc: TemplateDoc,
  columnId: string,
  patch: Partial<Column>,
): TemplateDoc {
  return { ...doc, columns: doc.columns.map((c) => (c.id === columnId ? { ...c, ...patch } : c)) };
}

/** A range the grid can edit inline: exactly one, numeric, for everyone. */
export function simpleRange(row: TestRow): RangeSpec | null | 'complex' {
  if (row.ranges.length === 0) return null;
  if (row.ranges.length > 1) return 'complex';
  const r = row.ranges[0] as RangeSpec;
  if (r.sex || r.ageMinDays !== undefined || r.ageMaxDays !== undefined) return 'complex';
  if (['between', 'lt', 'lte', 'gt', 'gte'].includes(r.type)) return r;
  return 'complex';
}

export function minMaxOf(r: RangeSpec | null): { min: string; max: string } {
  if (!r) return { min: '', max: '' };
  if (r.type === 'between') return { min: String(r.min), max: String(r.max) };
  if (r.type === 'gt' || r.type === 'gte') return { min: String(r.min), max: '' };
  if (r.type === 'lt' || r.type === 'lte') return { min: '', max: String(r.max) };
  return { min: '', max: '' };
}

/** Build the range from the Min and Max cells: both → between, one → one-sided. */
export function rangeFromMinMax(min: string, max: string): RangeSpec[] {
  const lo = min.trim() === '' ? null : Number(min);
  const hi = max.trim() === '' ? null : Number(max);
  if (lo !== null && Number.isNaN(lo)) return [];
  if (hi !== null && Number.isNaN(hi)) return [];
  if (lo !== null && hi !== null) return [{ type: 'between', min: lo, max: hi }];
  if (lo !== null) return [{ type: 'gte', min: lo }];
  if (hi !== null) return [{ type: 'lte', max: hi }];
  return [];
}

export function rangeText(row: TestRow): string {
  return row.ranges
    .map((r) => (r.sex ? `${r.sex}: ${describeRange(r)}` : describeRange(r)))
    .join('\n');
}

export function rangesFromText(text: string): RangeSpec[] {
  const r = parseRangeText(text);
  return r ? [r] : [];
}

/** Codes for new rows: TEST1, TEST2… never colliding with existing ones. */
export function nextCode(doc: TemplateDoc, base = 'TEST'): string {
  const codes = new Set(doc.rows.flatMap((r) => (r.kind === 'test' ? [r.code] : [])));
  let n = 1;
  while (codes.has(`${base}${n}`)) n++;
  return `${base}${n}`;
}

/** Suggest a formula code from a label: "Haemoglobin (Hb)" → "HB". */
export function codeFromLabel(label: string): string {
  const paren = /\(([A-Za-z0-9 ]{1,12})\)/.exec(label)?.[1];
  const base = (paren ?? label)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'T$1');
  return base.slice(0, 16) || 'TEST';
}
